import { useState, useEffect, useRef } from "react";
import "./App.css";

import { onAuthChange, signOut } from "./api/auth";
import { listUsers, subscribeUsers, markNotificationsRead, clearNotifications, updateProfile } from "./api/users";
import { listBooks, subscribeBooks, registerBook, getBook, findBookByIsbn } from "./api/books";
import { rememberGuestPick } from "./utils/guestPick";

import useStarfield from "./hooks/useStarfield";
import useNavigation from "./hooks/useNavigation";
import useBookData from "./hooks/useBookData";
import useUserPreferences from "./hooks/useUserPreferences";
import { getAutoFriends } from "./utils/similarity";
import { track } from "./utils/analytics";
import { pickDailyQuote } from "./utils/dailyQuote";
import decideDailyQuote from "./utils/dailyQuoteGate";
import { listPublicQuotes } from "./api/quotes";
import { filterRealUsers } from "./utils/firestoreHelpers";

import LoginScreen from "./screens/LoginScreen";
import HomeScreen from "./screens/HomeScreen";
import BookScreen from "./screens/BookScreen";
import UserScreen from "./screens/UserScreen";
import FriendsScreen from "./screens/FriendsScreen";
import TrailScreen from "./screens/TrailScreen";
import RecentBooksScreen from "./screens/RecentBooksScreen";
import IncompleteBooksScreen from "./screens/IncompleteBooksScreen";
import AddModal from "./modals/AddModal";
import MilestoneModal from "./modals/MilestoneModal";
import FirstBookModal from "./modals/FirstBookModal";
import DailyQuoteModal from "./modals/DailyQuoteModal";
import FeatureNoticeModal from "./modals/FeatureNoticeModal";
import BottomTabs from "./components/BottomTabs";
import { collectYears, MIN_POINTS } from "./utils/renderYearTrail";
import InstallBanner from "./components/InstallBanner";

export default function BookPlanetApp() {
  /* null 이면 닫힘. "book" 은 읽은/읽는 책, "wish" 는 읽고 싶은 책. */
  const [showModal, setShowModal] = useState(null);
  /** 책 화면에서 등록 화면을 열 때 이미 골라진 책. 닫으면 비운다. */
  const [prefillBook, setPrefillBook] = useState(null);
  /** 첫 책을 방금 담았으면 그 책. 안내를 한 번 띄우고 비운다. */
  const [firstBook, setFirstBook] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [guestMode, setGuestMode] = useState(false);
  /** 검색으로 책 페이지(muroo.kr/book/<isbn>)를 거쳐 들어왔을 때 그 책.
      주소에 ?book= 이 없으면 끝까지 null 이고, 아무 조회도 일어나지 않는다. */
  const [arrivedBook, setArrivedBook] = useState(null);
  const [users, setUsers] = useState([]);
  const [externalBook, setExternalBook] = useState(null);
  /** 하루 처음 들어온 날 뜨는 남의 문장. null 이면 오늘은 이미 봤거나 풀이 비었다. */
  const [dailyQuote, setDailyQuote] = useState(null);
  /* 공개 문장 목록. 오늘의 문장과 우주의 이야기 피드가 같은 것을 본다. */
  const [publicQuotes, setPublicQuotes] = useState([]);
  /** 새 기능 안내. 본 적 있으면 다시 뜨지 않는다. */
  const [featureNotice, setFeatureNotice] = useState(false);
  const starRef = useStarfield();

  const {
    screen, setScreen,
    selectedBook, selectedUser, bookViewUser,
    openTab, clearOpenTab,
    focusId, clearFocus,
    navigate, goBack, goHome, backToHome,
    handleTouchStart, handleTouchEnd,
  } = useNavigation();

  const {
    myBooks, setMyBooks,
    allBooksData, setAllBooksData,
    coversLoaded,
    appLoading, startLoading,
    handleAddBook, milestone, clearMilestone, reloadBooks,
    handleBookCompleted, handleBookRemoved, handleMemoChanged,
  } = useBookData(currentUser, setScreen);

  const {
    myWishlist, notifications, setNotifications, myFriends,
    reloadWishlist,
  } = useUserPreferences(currentUser);

  const autoFriends = getAutoFriends(myBooks, users, currentUser?.uid);

  /* 검색으로 들어온 사람 맞이하기.
     muroo.kr/book/<isbn> 을 보고 온 사람은 『데미안』을 찾다 온 것이지
     '독서 기록 앱' 을 찾다 온 것이 아니다. 그 책을 로그인 화면에 그대로
     두고, 눌러본 책으로도 기억해 둔다 — 가입 직후 첫 책으로 내밀어진다
     (utils/guestPick 이 하던 일을 그대로 쓴다).
     주소를 건드리지는 않는다. 지우면 뒤로 가기로 돌아온 사람에게 사라진다. */
  useEffect(() => {
    const isbn = new URLSearchParams(window.location.search).get("book");
    if (!isbn) return;
    let alive = true;
    findBookByIsbn(isbn).then((b) => {
      if (!alive || !b) return;
      setArrivedBook(b);
      rememberGuestPick(b);
      track("arrived_from_book_page", { isbn });
    });
    return () => { alive = false; };
  }, []);

  /* 가입은 DB 트리거가 만들어 클라이언트에 걸 자리가 없다. 프로필이 방금
     생겼는지로 본다 — 기기에 표시를 남기는 방식은 다른 기기에서 로그인한
     기존 사용자까지 가입으로 세게 된다. */
  useEffect(() => {
    const uid = currentUser?.uid;
    if (!uid || currentUser?.isAnonymous) return;
    const me = (users || []).find((u) => u.id === uid);
    if (!me?.createdAt) return;
    if (Date.now() - new Date(me.createdAt).getTime() > 5 * 60 * 1000) return;
    const key = `muroo_signup_sent_${uid}`;
    try {
      if (localStorage.getItem(key)) return;
      localStorage.setItem(key, "1");
    } catch { /* 막힌 브라우저에서는 한 번 더 셀 수 있다 — 그 편이 낫다 */ }
    track("sign_up");
  }, [currentUser, users]);

  /* 첫 책이 이 앱의 진짜 전환점이다. 담기 직전에 0권이었는지로 센다 —
     처음 불러올 때 []→[34권] 도 0에서 늘어난 것이라 상태 변화를 지켜보면
     기존 사용자가 매번 첫 책을 담은 것으로 세어진다. */
  /* 봤다는 표시는 계정마다 따로 둔다. 예전엔 기기에 하나라, 한 계정이
     닫으면 같은 브라우저에서 새로 가입한 계정은 모두 건너뛰었다 —
     테스트하던 기기에서는 다시는 볼 수 없었다. */
  const firstBookKey = (uid) => `muroo-seen-first-book:${uid || "guest"}`;
  const handleAddBookTracked = (newBook, isNewToMuroo) => {
    const isFirst = myBooks.length === 0;
    if (isFirst) track("first_book");
    const r = handleAddBook(newBook, isNewToMuroo);
    /* 첫 책 뒤에 한 번만. 책을 지웠다가 다시 담아도 다시 뜨지 않게
       기기에 표시를 남긴다. */
    if (isFirst) {
      let seen = false;
      try { seen = !!localStorage.getItem(firstBookKey(currentUser?.uid)); } catch { seen = false; }
      if (!seen) setFirstBook(newBook);
    }
    return r;
  };

  const closeFirstBook = () => {
    try { localStorage.setItem(firstBookKey(currentUser?.uid), "1"); } catch { /* 못 남겨도 이번엔 닫는다 */ }
    setFirstBook(null);
  };

  /* 게스트로 구경하다 누른 책을 가입 직후 한 번에 담아준다.
     제목·저자로 upsert 하므로 이미 MUROO 에 있는 책이면 그 책에 붙는다. */
  const handleQuickAdd = async (pick) => {
    const uid = currentUser?.uid;
    if (!uid || !pick?.title) return;
    const { id, isNewToMuroo } = await registerBook(
      { title: pick.title, author: pick.author, genre: pick.genre, cover: pick.cover, isbn: pick.isbn || null },
      uid
    );
    const book = await getBook(id);
    handleAddBookTracked(book ?? { id, title: pick.title, author: pick.author, cover: pick.cover, genre: pick.genre ?? "기타" }, isNewToMuroo);
    /* 책 화면으로 데려간다 — 담는 것이 끝이 아니라 거기서 기록이 시작된다. */
    navigate("book", { bookId: id });
  };

  // 새 기능 안내. 쓸 수 있는 사람에게만 띄운다 — 궤적을 그릴 해가
  // 없는 사람에게 알려봐야 눌러도 막혀 있다.
  const FEATURE_KEY = "muroo-seen-year-trail";
  useEffect(() => {
    if (!currentUser || currentUser.isAnonymous || !myBooks.length) return;
    try {
      if (localStorage.getItem(FEATURE_KEY)) return;
    } catch {
      return; // 저장이 막힌 브라우저에서는 매번 뜨느니 띄우지 않는다
    }
    const usable = collectYears(myBooks, currentUser.uid)
      .some((y) => y.points.length >= MIN_POINTS);
    if (usable) setFeatureNotice(true);
  }, [currentUser, myBooks]);

  const closeFeatureNotice = () => {
    try { localStorage.setItem(FEATURE_KEY, "1"); } catch { /* 못 남겨도 이번엔 닫는다 */ }
    setFeatureNotice(false);
  };

  // 하루에 한 번 남의 문장을 띄울지는 decideDailyQuote 가 정한다
  // (왜 책이 도착한 순간 정하는지는 utils/dailyQuoteGate.js 에 적어 두었다).
  // 게스트에게는 띄우지 않는다 — 문장은 게스트에게 감추고 있다.
  // 첫 책 축하와도 겹치지 않는다: 0권일 때 이미 'skip' 으로 정해진다.
  /* 공개 문장은 한 번만 받는다. 오늘의 문장과 피드가 같은 목록을 쓰므로
     따로 부르면 같은 200줄을 두 번 받게 된다. */
  useEffect(() => {
    if (!currentUser || currentUser.isAnonymous) return undefined;
    let alive = true;
    listPublicQuotes().then((pool) => { if (alive) setPublicQuotes(pool || []); });
    return () => { alive = false; };
  }, [currentUser]);

  const dailyDecided = useRef(false);
  useEffect(() => {
    if (!currentUser || currentUser.isAnonymous || dailyDecided.current) return;
    const verdict = decideDailyQuote({
      coversLoaded,
      bookCount: myBooks.length,
      quoteCount: publicQuotes.length,
      blocked: featureNotice,
    });
    if (verdict === "wait") return;
    dailyDecided.current = true;
    if (verdict === "show") setDailyQuote((prev) => prev ?? pickDailyQuote(publicQuotes, currentUser.uid));
  }, [currentUser, featureNotice, coversLoaded, myBooks.length, publicQuotes]);

  /* 탭바가 있는 동안에는 화면 아래에 자리를 비우고, 떠 있는 버튼을 올린다.
     CSS 로만 처리하면 로그인 전 화면까지 여백이 생긴다. */
  const hasTabs = !!currentUser && !currentUser.isAnonymous && !guestMode;
  useEffect(() => {
    document.body.classList.toggle("has-tabbar", hasTabs);
    return () => document.body.classList.remove("has-tabbar");
  }, [hasTabs]);

  // 인증 상태 감시.
  // 프로필 행 생성은 auth.users 트리거(handle_new_user)가 처리하므로
  // 예전처럼 클라이언트에서 setDoc 할 필요가 없다.
  useEffect(() => {
    const unsub = onAuthChange((user) => {
      if (user) {
        startLoading();
        setCurrentUser(user);
      } else {
        setCurrentUser(null);
        setScreen("login");
      }
    });
    return () => unsub();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 게스트 모드 데이터 로드.
  // 시드/고아 정리 함수들은 전부 사라졌다 — DB 트리거와 cascade 가 대신한다.
  useEffect(() => {
    if (!guestMode) return;
    let alive = true;
    // 게스트는 책만 본다. 프로필(이름·핸들)은 가져오지 않으며,
    // RLS 도 로그인하지 않은 요청에는 프로필을 내주지 않는다.
    const load = async () => {
      try {
        const books = await listBooks();
        if (!alive) return;
        setAllBooksData(books);
        setScreen("home");
      } catch (e) {
        console.error("게스트 데이터 로드 실패:", e.message);
      }
    };
    load();
    const unsubBooks = subscribeBooks(load);
    return () => { alive = false; unsubBooks(); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [guestMode]);

  // 유저 목록 (실시간)
  useEffect(() => {
    if (!currentUser) return;
    let alive = true;
    const load = async () => {
      try {
        const list = await listUsers();
        if (alive) setUsers(filterRealUsers(list));
      } catch (e) {
        console.error("유저 목록 로드 실패:", e.message);
      }
    };
    load();
    const unsub = subscribeUsers(load);
    return () => { alive = false; unsub(); };
  }, [currentUser]);

  // 프로필 수정 후 목록을 다시 읽는다 (예전엔 Firebase 유저 객체를 reload 했다)
  const reloadUsers = async () => {
    try {
      setUsers(filterRealUsers(await listUsers()));
    } catch (e) {
      console.error("유저 목록 갱신 실패:", e.message);
    }
  };

  const handleLogout = async () => {
    await signOut();
  };

  return (
    <>
      <canvas id="starfield" ref={starRef} />
      <div id="bp-root" onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd}>
        {(appLoading || screen === "loading") && (
          <div className="bp-layer loading-screen">
            <div className="loading-planet">🪐</div>
            <div className="loading-text">loading universe...</div>
          </div>
        )}
        {!appLoading && screen === "login" && (
          <LoginScreen onGuest={() => setGuestMode(true)} book={arrivedBook} />
        )}
        {screen === "home" && (
          <HomeScreen
            books={myBooks}
            allBooks={allBooksData}
            autoFriends={autoFriends}
            users={users}
            coversLoaded={coversLoaded}
            friendCount={(() => {
              const autoIds = new Set(autoFriends.map(f => f.id));
              const iAddedOther = (users || []).filter(u => myFriends.includes(u.id) && !autoIds.has(u.id) && u.id !== currentUser?.uid).length;
              const addedMe = (users || []).filter(u => u.id !== currentUser?.uid && (u.manualFriends || []).includes(currentUser?.uid) && !autoIds.has(u.id) && !myFriends.includes(u.id)).length;
              return autoFriends.length + iAddedOther + addedMe;
            })()}
            onBookClick={(id) => navigate("book", { bookId: id })}
            onExternalBookClick={(bookData) => { setExternalBook(bookData); navigate("book", { bookId: bookData.id }); }}
            onUserClick={(id) => navigate("user", { userId: id })}
            onAddClick={() => setShowModal("book")}
            onQuickAdd={handleQuickAdd}
            onAddWishClick={() => setShowModal("wish")}
            onFriendsClick={() => navigate("friends")}
            onTrailClick={() => navigate("trail")}
            onRecentClick={() => navigate("recent")}
            /* 홈의 '올해 N권' → 내 행성의 월별 탭. 같은 값을 두 곳에서
               보게 하는 대신 자세히 보는 자리로 데려간다. */
            onMonthlyClick={() => navigate("user", { userId: "me", tab: "month" })}
            onMyTab={(tab) => navigate("user", { userId: "me", tab })}
            /* 북BTI 결과. 유형은 프로필에 남아 행성 배지와 "같은 유형" 에 쓰인다. */
            onSaveBookType={async (t) => {
              try { await updateProfile(currentUser.uid, { bookType: t }); reloadUsers(); }
              catch (e) { console.error("북BTI 유형 저장 실패:", e.message); }
            }}
            onLogout={guestMode ? () => { setGuestMode(false); setScreen("login"); } : handleLogout}
            currentUser={currentUser}
            guestMode={guestMode}
            myFriends={myFriends}
            reloadFriends={reloadWishlist}
            notifications={notifications}
            onMarkNotificationsRead={async () => {
              if (!currentUser || currentUser.isAnonymous) return;
              await markNotificationsRead(currentUser.uid);
              setNotifications(notifications.map(n => ({ ...n, read: true })));
            }}
            onClearNotifications={async () => {
              if (!currentUser || currentUser.isAnonymous) return;
              try {
                await clearNotifications(currentUser.uid);
                setNotifications([]);
              } catch (e) {
                console.error("알림 삭제 실패:", e.message);
              }
            }}
            onRecommendToggle={(bookId, recommended) => {
              const uid = currentUser?.uid;
              if (!uid) return;
              const updater = prev => prev.map(b =>
                b.id === bookId
                  ? { ...b, recommendedBy: recommended
                      ? { ...(b.recommendedBy || {}), [uid]: true }
                      : Object.fromEntries(Object.entries(b.recommendedBy || {}).filter(([k]) => k !== uid))
                    }
                  : b
              );
              setMyBooks(updater);
              setAllBooksData(updater);
            }}
          />
        )}
        {screen === "book" && (
          <BookScreen
            bookId={selectedBook}
            books={myBooks}
            allBooks={allBooksData}
            externalBook={externalBook}
            users={users}
            currentUser={currentUser}
            viewAsUserId={bookViewUser}
            onBack={goBack}
            onUserClick={(id) => navigate("user", { userId: id })}
            onBookUpdated={reloadBooks}
            onBookCompleted={handleBookCompleted}
            onBookRemoved={handleBookRemoved}
            onMemoAdded={handleMemoChanged}
            onBookClick={(id) => navigate("book", { bookId: id })}
            onAddToMine={(book) => { setPrefillBook(book); setShowModal("book"); }}
            onGoHome={goHome}
            openTab={openTab}
            clearOpenTab={clearOpenTab}
            focusId={focusId}
            clearFocus={clearFocus}
            /* 내 행성 = 홈. 앞으로 쌓으면 책 ↔ 내 행성 고리가 생긴다 */
            onGoMine={backToHome}
          />
        )}
        {screen === "friends" && (
          <FriendsScreen
            onBack={goBack}
            onUserClick={(id) => navigate("user", { userId: id })}
            onBookClick={(id) => navigate("book", { bookId: id })}
            onStoryClick={(bookId, memoId) => navigate("book", { bookId, tab: "public", focusId: memoId })}
            /* 문장은 "q:" 를 붙여 보낸다. 기록 id 와 문장 id 가 둘 다 숫자여서
               그냥 보내면 3번 기록과 3번 문장을 구분할 수 없다. */
            onQuoteClick={(bookId, quoteId) => navigate("book", { bookId, tab: "public", focusId: `q:${quoteId}` })}
            publicQuotes={publicQuotes}
            myBooks={myBooks}
            allBooks={allBooksData}
            autoFriends={autoFriends}
            myFriends={myFriends}
            users={users}
            currentUser={currentUser}
            reloadFriends={reloadWishlist}
          />
        )}
        {screen === "trail" && (
          <TrailScreen
            allBooks={allBooksData}
            users={users}
            currentUser={currentUser}
            onBack={goBack}
            onBookClick={(id) => navigate("book", { bookId: id })}
            onUserClick={(id) => navigate("user", { userId: id })}
          />
        )}
        {screen === "recent" && (
          <RecentBooksScreen
            allBooks={allBooksData}
            users={users}
            currentUser={currentUser}
            autoFriends={autoFriends}
            myFriends={myFriends}
            onBack={goBack}
            onBookClick={(id) => navigate("book", { bookId: id })}
          />
        )}
        {screen === "incomplete-books" && (
          <IncompleteBooksScreen
            allBooks={allBooksData}
            users={users}
            currentUser={currentUser}
            onBack={goBack}
            onBookClick={(id) => navigate("book", { bookId: id })}
          />
        )}
        {screen === "user" && (
          <UserScreen
            userId={selectedUser}
            openTab={openTab}
            clearOpenTab={clearOpenTab}
            focusId={focusId}
            clearFocus={clearFocus}
            myBooks={myBooks}
            allBooks={allBooksData}
            users={users}
            currentUser={currentUser}
            onBack={goBack}
            onBookClick={(id) => navigate("book", { bookId: id, viewAsUser: (selectedUser === "me" || selectedUser === currentUser?.uid) ? undefined : selectedUser })}
            onUserUpdated={reloadUsers}
            myWishlist={myWishlist}
            myFriends={myFriends}
            reloadWishlist={reloadWishlist}
          />
        )}
        {/* 하단 탭바. 실제로 있는 화면만 칸으로 둔다. */}
        {hasTabs && ["home", "friends", "trail", "user"].includes(screen) && (
          <BottomTabs
            active={screen === "user" && (selectedUser === "me" || selectedUser === currentUser?.uid) ? "mine" : screen}
            onSelect={(k) => {
              if (k === "home") goHome();
              else if (k === "mine") navigate("user", { userId: "me" });
              else navigate(k);
            }}
          />
        )}
        {/* 홈에서만, 가입한 사람에게만 권한다.
            로그인 화면에 띄우면 'Google로 계속하기' 를 그대로 덮는다(띠가
            화면 476~716px 을 차지해 버튼 세 개가 그 안에 들어간다).
            게스트에게도 띄우지 않는다 — 홈 화면에 추가하는 것보다 가입이
            먼저다. */}
        {hasTabs && screen === "home" && <InstallBanner />}
        {showModal && (
          <AddModal
            onClose={() => { setShowModal(null); setPrefillBook(null); }}
            onAddBook={handleAddBookTracked}
            /* 보고 있던 책을 그대로 등록한 것이면 같은 화면으로 다시
               데려가지 않는다. 히스토리만 한 칸 쌓이고 화면은 그 자리다. */
            onBookClick={(id) => {
              /* 보고 있던 책을 그대로 담았으면 같은 화면에 머문다 */
              if (screen === "book" && selectedBook === id) return;
              navigate("book", { bookId: id });
            }}
            currentUser={currentUser}
            allBooks={allBooksData}
            wantToRead={showModal === "wish"}
            prefill={prefillBook}
          />
        )}
        <MilestoneModal milestone={milestone} onClose={clearMilestone} />
        {/* 첫 책 안내. 111번째 축하가 뜬 순간에는 양보한다. */}
        {!milestone && firstBook && (
          <FirstBookModal
            book={firstBook}
            users={users}
            uid={currentUser?.uid}
            onUserClick={(id) => { closeFirstBook(); navigate("user", { userId: id }); }}
            onGoMine={() => { closeFirstBook(); navigate("user", { userId: "me" }); }}
            onClose={closeFirstBook}
          />
        )}
        {/* 111번째 축하가 먼저다. 기능 안내는 내일 봐도 된다. */}
        {!milestone && featureNotice && (
          <FeatureNoticeModal
            onClose={closeFeatureNotice}
            onGo={() => { closeFeatureNotice(); navigate("user", { userId: "me" }); }}
          />
        )}
        {/* 기념 화면이 뜬 순간에는 양보한다. 겹치면 둘 다 값이 떨어진다. */}
        {!milestone && !featureNotice && (
          <DailyQuoteModal
            quote={dailyQuote}
            onClose={() => setDailyQuote(null)}
            onUserClick={(id, quoteId) => navigate("user", { userId: id, tab: "quotes", focusId: quoteId })}
          />
        )}
      </div>
    </>
  );
}
